import { WarningCircleIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { useCallback } from 'react'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { WithRequired } from '../../types.ts'
import { useDeleteRepository } from '../../utils/api-client/api-hooks.ts'
import { Repository } from '../../utils/api-client/api-schemas.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon.tsx'
import * as styles from './delete-repository-modal.css.ts'

type DeleteRepositoryModalProps = {
  repository: Pick<Repository, 'id' | 'name' | 'type' | 'full_name'>
  onCloseDeleteRepositoryDialog?: () => unknown
  onDeleteRepositorySuccess?: () => unknown
}

export const DeleteRepositoryModal: React.FC<DeleteRepositoryModalProps> = ({
  repository,
  onCloseDeleteRepositoryDialog = () => {},
  onDeleteRepositorySuccess = () => {},
}) => {
  const { addToast } = useToastService()
  const showMutationError = useShowMutationError({ prefix: 'Unable to delete repository' })
  const { mutate: deleteRepository } = useDeleteRepository({
    onSuccess: async () => {
      onDeleteRepositorySuccess()
      addToast({
        message: <>Repository deleted successfully.</>,
        variant: 'success',
      })
    },
    onError: showMutationError,
  })
  const handleDeleteRepository = useCallback(
    () => deleteRepository({ repositoryId: repository.id }),
    [deleteRepository, repository.id]
  )

  return (
    <DeleteRepositoryModalPure
      repository={repository}
      onCloseDeleteRepositoryDialog={onCloseDeleteRepositoryDialog}
      onDeleteRepository={handleDeleteRepository}
    />
  )
}

type DeleteRepositoryModalPureProps = WithRequired<DeleteRepositoryModalProps, 'onCloseDeleteRepositoryDialog'> & {
  onDeleteRepository: () => unknown
}

const DeleteRepositoryModalPure: React.FC<DeleteRepositoryModalPureProps> = ({
  repository,
  onCloseDeleteRepositoryDialog,
  onDeleteRepository,
}) => {
  return (
    <Dialog.Root open={true}>
      <Dialog.Overlay className={styles.alertDialogOverlay} />
      <Dialog.Content className={styles.alertDialogContent}>
        <Dialog.Title className={styles.alertDialogTitle}>
          <WarningCircleIcon className={styles.alertDialogIcon} size={32} weight="fill" />
          Delete repository?
        </Dialog.Title>
        <Dialog.Description className={styles.alertDialogDescription} asChild>
          <div>
            <p className={styles.alertDialogDescriptionParagraph}>
              You are about to delete the following repository, including its associated analysis data:
            </p>
            <div className={styles.alertDialogRepository}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <SCMIcon type={repository.type} size="medium" />
                {repository.full_name}
              </span>
            </div>
            <p className={styles.alertDialogDescriptionParagraph}>
              This action cannot be undone, and the data cannot be retrieved. Do you wish to proceed?
            </p>
          </div>
        </Dialog.Description>
        <div className={styles.alertDialogButtons}>
          <GhostButton type="secondary" onClick={onCloseDeleteRepositoryDialog}>
            Cancel
          </GhostButton>
          <DefaultButton
            buttonType="destructive"
            onClick={onDeleteRepository}
            data-testid="delete-repository-modal-delete-button"
          >
            Delete repository
          </DefaultButton>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  )
}
